import { useState } from "react";
import { WarningCircle, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { fmtBRL } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export const ZeroCostBanner = ({ products }) => {
  if (!products?.length) return null;
  return (
    <div data-testid="zero-cost-banner"
      className="flex items-start gap-2 bg-amber-100 border border-amber-400 text-amber-900 rounded-md px-3 py-2.5">
      <WarningCircle size={18} weight="fill" className="shrink-0 mt-0.5 text-amber-600" />
      <div className="text-xs sm:text-sm leading-snug">
        <b>{products.length} produto{products.length > 1 ? "s" : ""} sem custo cadastrado</b> — o lucro deste pedido será calculado errado.
        <span data-testid="zero-cost-banner-names" className="block text-[11px] mt-0.5 text-amber-800">
          {products.map((p) => p.name).join(" · ")}
        </span>
      </div>
    </div>
  );
};

export const ZeroCostItemWarning = ({ product, canFix, onFix, testId }) => (
  <p data-testid={testId}
    className="mt-1 flex items-center gap-1.5 flex-wrap text-[11px] text-amber-900 bg-amber-100 border border-amber-400 rounded px-2 py-1">
    <WarningCircle size={13} weight="fill" className="shrink-0 text-amber-600" />
    <span><b>Sem custo cadastrado</b> — o lucro deste pedido sairá errado.</span>
    {canFix ? (
      <button type="button" data-testid={`${testId}-fix-button`} onClick={onFix}
        className="font-bold underline underline-offset-2 text-amber-950 hover:text-amber-700 transition-colors">
        Cadastrar custo
      </button>
    ) : (
      <span className="text-amber-700">Peça a um administrador para cadastrar o custo de "{product.name}".</span>
    )}
  </p>
);

export const QuickCostDialog = ({ product, onClose, onSaved }) => {
  const [cost, setCost] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    const v = parseFloat(String(cost).replace(",", "."));
    if (!v || v <= 0) {
      toast.error("Informe um custo maior que zero");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/products/${product.id}/cost`, { avg_cost: v });
      const nOrders = data.cost_backfill?.orders?.length || 0;
      toast.success(
        `Custo de "${product.name}" cadastrado: ${fmtBRL(data.avg_cost)}` +
        (nOrders ? ` — lucro de ${nOrders} pedido(s) antigo(s) corrigido automaticamente` : ""),
        { duration: 7000 });
      onSaved(product.id, data.avg_cost);
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="quick-cost-dialog" className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-display text-base">Cadastrar custo do produto</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-zinc-300">
            <span className="font-semibold text-zinc-100">{product.name}</span>
            {product.sku ? <span className="text-zinc-500"> · {product.sku}</span> : null}
          </p>
          <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2.5 py-1.5">
            Sem o custo, o Lucro Real deste e de pedidos futuros sai errado. Ao salvar, pedidos antigos deste produto também serão corrigidos automaticamente.
          </p>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 block mb-1">
              Custo unitário (R$)
            </label>
            <input data-testid="quick-cost-input" type="number" min="0.01" step="0.01" autoFocus
              className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="0,00" value={cost} onChange={(e) => setCost(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(e); } }} />
          </div>
          <div className="flex gap-2 justify-end pt-1">
            <button type="button" data-testid="quick-cost-cancel" onClick={onClose}
              className="px-4 py-2 text-sm rounded border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors">
              Cancelar
            </button>
            <button type="button" data-testid="quick-cost-save" disabled={saving} onClick={save}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white font-medium transition-colors">
              {saving ? (<><CircleNotch size={15} className="animate-spin" /> Salvando...</>) : "Salvar custo"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
